/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { normalizeContentLinksData } from '../normalizers/normalizeContentLinksData';

import type {
	useContentLinksDetailsPanelContentLinksQuery as useContentLinksDetailsPanelContentLinksQuery$data,
	useContentLinksDetailsPanelContentLinksQueryVariables as useContentLinksDetailsPanelContentLinksQuery$variables,
} from './__types__/useContentLinksDetailsPanelContentLinksQuery';

const useContentLinksDetailsPanelContentLinksQuery = graphql`
	query useContentLinksDetailsPanelContentLinksQuery(
		$contentId: ID!
		$status: String!
		$limit: Int!
	) {
		singleContent(id: $contentId, status: [$status]) {
			id
			incomingLinks(first: $limit) {
				count
				pageInfo {
					hasNextPage
				}
			}
			outgoingLinks {
				internalOutgoingLinks(first: $limit) {
					count
					pageInfo {
						hasNextPage
					}
				}
				externalOutgoingLinks(first: $limit) {
					count
					pageInfo {
						hasNextPage
					}
				}
			}
		}
	}
`;
export const useContentLinks = (linkLimit: number) => {
	const [contentId] = usePageContentId();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus();
	const { data, loading, error, refetch } = useQuery<
		useContentLinksDetailsPanelContentLinksQuery$data,
		useContentLinksDetailsPanelContentLinksQuery$variables
	>(useContentLinksDetailsPanelContentLinksQuery, {
		variables: {
			contentId: contentId ?? '',
			status: contentStatus ?? '',
			limit: linkLimit,
		},
		skip: isContentStatusLoading || !!contentStatusError || !contentStatus || !contentId,
	});
	const normalizedData = useMemo(() => normalizeContentLinksData(data), [data]);

	return {
		data: normalizedData,
		loading,
		error: contentStatusError || error,
		refetch: !!contentStatusError ? refetchContentStatus : refetch,
	};
};
